<script setup>
import {onMounted, ref, watch} from 'vue';
import {codePreview, formatDate} from '@/helper';
import {Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot} from '@headlessui/vue'
import DocumentTemplateCategoryService from '@/services/DocumentTemplateCategoryService';
import DocumentTemplateService from '@/services/DocumentTemplateService';
import Multiselect from "@vueform/multiselect";
import IntermediaryPersonsService from '@/services/IntermediaryPersonsService';
import IntermediaryService from '@/services/IntermediaryService';
import {createConfirmDialog} from 'vuejs-confirm-dialog';
import ModalDialog from '@/components/modals/ModalDialog.vue';
import router from '@/router';
import {useToast} from 'vue-toastification';
import {useI18n} from 'vue-i18n';

const {t} = useI18n()
const toast = useToast();

const props = defineProps({
  documentTemplate: {type: Object, required: false},
});

const name = ref(props.documentTemplate?.name);
const description = ref(props.documentTemplate?.description);
const categoryIds = ref(props.documentTemplate?.categories?.map(category => category.id));
const codeFormat = ref(props.documentTemplate?.code_format);
const isActive = ref(props.documentTemplate?.is_active ?? true);
const categories = ref([]);
const showIntermediaries = ref(false);
const intermediaryIds = ref(presetIntermediaries());
const intermediariesPeople = ref([]);
const labelText7 = ref(props.documentTemplate?.label_text_7);

const isOpen = ref(true)
const emit = defineEmits(['confirm', 'cancel']);
const closeModalWith = (value) => {
  isOpen.value = false;
  setTimeout(() => {
    if (value === 'confirm') {
      emit('confirm', {
        id: props.documentTemplate?.id,
        name: name.value,
        description: description.value,
        code_format: codeFormat.value,
        is_active: isActive.value,
        category_ids: categoryIds.value,
        intermediary_ids: showIntermediaries.value ? intermediaryIds.value : null,
        label_text_7: labelText7.value,
      });
    } else {
      emit('cancel');
    }
  }, 300);
}

function openModal() {
  isOpen.value = true
}

function fetchCategories() {
  DocumentTemplateCategoryService.getAll().then(response => {
    categories.value = response.map(category => {
      return {
        value: category.id,
        label: category.name
      }
    });
  });
}

function fetchIntermediariesPersons() {
  IntermediaryService.getAll().then(response => {
    intermediariesPeople.value = response.map(intermediary => {
      return {
        value: intermediary.id,
        label: intermediary.label
      }
    });
  });
}

function presetIntermediaries() {
  if (props.documentTemplate?.intermediary_ids?.length >= 0) {
    showIntermediaries.value = true;
    return props.documentTemplate?.intermediary_ids;
  }

  return [];
}

watch(name, (newValue) => {
  const nameArray = newValue.split(' ');
  let finalCode = '';

  if (nameArray.length > 1) {
    nameArray.forEach(element => {
      if (typeof element === 'string' && !isNaN(element)) {
        finalCode += element;
      } else {
        finalCode += element.charAt(0);
      }
    });
  }

  if (nameArray.length === 1) {
    finalCode = nameArray[0].substring(0, 3);
  }

  codeFormat.value = finalCode.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/gi, '') + '_%Y%_%NMB_4%';
});

function deleteDocumentTemplate() {
  const {reveal, onConfirm} = createConfirmDialog(ModalDialog, {
    title: t('createEditDocumentTemplateModal.delete_title'),
    message: t('createEditDocumentTemplateModal.delete_confirm'),
  });
  onConfirm(() => {
    DocumentTemplateService.deleteDocumentTemplate(props.documentTemplate.id).then(() => {
      toast.success(t('createEditDocumentTemplateModal.delete_success'));
      closeModalWith('cancel');
      router.push({name: 'DocumentTemplates'});
    });
  });
  reveal();
}

onMounted(() => {
  fetchCategories();
  fetchIntermediariesPersons();
});
</script>

<template>
  <TransitionRoot appear :show="isOpen" as="template">
    <Dialog as="div"  class="modal show d-block" style="z-index: 1055;">
      <div class="modal-backdrop fade show"></div>
      <div class="modal-dialog modal-dialog-centered modal-lg" style="z-index: 1055">
        <div class="modal-content">
            <DialogPanel class="modal-body p-4 rounded-lg shadow-lg">
              <DialogTitle as="h3" class="modal-header fs-5 fw-bold">
                {{ t('createEditDocumentTemplateModal.new_template') }}
              </DialogTitle>

              <div class="modal-body">
                <div class="mb-3">
                  <div class="form-group">
                  <label for="nameInput" class="form-label">{{ t('createEditDocumentTemplateModal.name') }}</label>
                  <input v-model="name" id="nameInput" class="form-control" />
                  </div>
                </div>
                <div class="mb-3">
                  <label for="descriptionTextarea" class="form-label">{{ t('createEditDocumentTemplateModal.description') }}</label>
                  <textarea v-model="description" id="descriptionTextarea" class="form-control" rows="3" />
                </div>
                <div class="mb-3">
                  <div class="form-group">
                    <label for="categoriesSelect" class="form-label">{{ t('createEditDocumentTemplateModal.category') }}</label>
                    <Multiselect id="categoriesSelect" v-model="categoryIds" :options="categories" :close-on-select="false" :hide-selected="false" :placeholder="t('createEditDocumentTemplateModal.choose_category')" mode="tags"/>
                  </div>
                </div>
                <div class="mb-3 position-relative">
                  <label for="codeFormatInput" class="form-label">{{ t('createEditDocumentTemplateModal.numbering_format') }}</label>
                  <input v-model="codeFormat" id="codeFormatInput" class="form-control" />
<!--                  <div class="position-absolute top-0 end-0 me-2 mt-1 cursor-pointer" data-bs-toggle="tooltip" title="Klíče formátu číslování...">
                    <span class="mdi mdi-information-variant-circle fs-4"></span>
                  </div>-->
                </div>
                <div class="mb-3 position-relative">
                  <label for="labelText7Input" class="form-label">{{ t('createEditDocumentTemplateModal.label_text_7') }}</label>
                  <input v-model="labelText7" id="labelText7Input" class="form-control" />
                </div>
                <div class="text-end text-muted small">
                  {{ t('createEditDocumentTemplateModal.sample')}} <span class="fw-semibold">{{ codePreview(codeFormat) }}</span>
                </div>
                <div class="form-check">
                  <input type="checkbox" v-model="isActive" id="isActiveCheck" class="form-check-input" />
                  <label for="isActiveCheck" class="form-check-label">{{ t('createEditDocumentTemplateModal.active') }}</label>
                </div>

                <div class="form-check">
                  <input type="checkbox" v-model="showIntermediaries" id="showIntermediariesCheck" class="form-check-input" />
                  <label for="showIntermediariesCheck" class="form-check-label">{{ t('createEditDocumentTemplateModal.template_for_certain_intermediaries') }}</label>
                </div>
                <div v-if="showIntermediaries" class="mb-3">
                  <div class="form-group">
                    <label for="intermediariesSelect" class="form-label">{{ t('createEditDocumentTemplateModal.intermediaries') }}</label>
                    <Multiselect id="intermediariesSelect" v-model="intermediaryIds" :options="intermediariesPeople" :close-on-select="false" :hide-selected="false" :placeholder="t('createEditDocumentTemplateModal.choose_intermediaries')" mode="tags"/>
                  </div>
                </div>
              </div>

              <div class="modal-footer">
                <button class="btn btn-link text-decoration-underline text-dark" @click="closeModalWith('cancel')">{{ t('createEditDocumentTemplateModal.cancel') }}</button>
                <button
                  v-if="props.documentTemplate?.id && props.documentTemplate?.generated_count < 1"
                  class="btn btn-danger btn-md"
                  @click.prevent="deleteDocumentTemplate"
                >{{ t('createEditDocumentTemplateModal.delete') }}</button>
                <button class="btn btn-primary btn-sm" @click="closeModalWith('confirm')">{{ t('createEditDocumentTemplateModal.save') }}</button>
              </div>
            </DialogPanel>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

